import { Dayjs } from 'dayjs';
import { STATION_TIME_ZONE, atStation, formatAtStation, pickedDay, stationCalendarDay, stationInstant } from './stationTime';

// Venue bookings allow a free hourly pick (location opening hours are
// ignored): start in 00..22. When the booking starts today, past hours
// are filtered out — the lowest selectable start is `currentHour + 1`.
// "Today" and the current hour are the station's (ADR-0019) — in
// `timeZone`, the booked location's zone (#3219) — not the operator's
// browser clock.
//
// End behavior depends on whether the booking is same-day:
//  - same day (or endDate omitted): end in (start+1)..23, so end is
//    always after start. Before a start is picked, end starts at the
//    lowest hour reachable from any valid start.
//  - multi-day: end in 00..23, since the end is on a later date and
//    can numerically be earlier or later than the start hour.
export const getAvailableBookingTimes = (
    selectedStartTime?: string | null,
    startDate?: Dayjs | null,
    endDate?: Dayjs | null,
    timeZone: string = STATION_TIME_ZONE,
) => {
    let minStartHour = 0;
    if (startDate) {
        const now = new Date();
        if (pickedDay(startDate) === stationCalendarDay(now, timeZone)) {
            minStartHour = atStation(now, timeZone).hour + 1;
        }
    }

    const availableStartTimes: string[] = [];
    for (let hour = minStartHour; hour <= 22; hour++) {
        availableStartTimes.push(`${hour.toString().padStart(2, '0')}:00`);
    }

    const isSameDay = !endDate || !startDate || startDate.isSame(endDate, 'day');

    let minEndHour: number;
    if (isSameDay) {
        // Once a start is picked, clamp to (startHour + 1)..23. Before
        // that, use the widest valid range that's still reachable from
        // some allowable start.
        minEndHour = Math.max(1, minStartHour + 1);
        if (selectedStartTime) {
            const startHour = Number(selectedStartTime.split(':')[0]);
            if (!Number.isNaN(startHour)) {
                minEndHour = startHour + 1;
            }
        }
    } else {
        // Multi-day: end is on a later date, so any hour is valid.
        minEndHour = 0;
    }

    const availableEndTimes: string[] = [];
    for (let hour = minEndHour; hour <= 23; hour++) {
        availableEndTimes.push(`${hour.toString().padStart(2, '0')}:00`);
    }

    return {
        availableStartTimes,
        availableEndTimes,
    };
};

const HH_MM_SS = /^(\d{2}):(\d{2})(:\d{2}(\.\d+)?)?$/;
const ISO_TIME_OF_DAY = /T(\d{2}):(\d{2})/;

/**
 * Renders a `pricescale` time bound as the wall-clock time it means to an
 * operator at the station.
 *
 * The stored value is not the shape the column type suggests: Prisma returns
 * `Time(6)` as a Date, so it reaches the client as a full ISO instant at the
 * epoch — `'1970-01-01T13:00:00.000Z'`, not `'13:00:00'`. Rendering that
 * verbatim is what #1898 shipped, and it is unreadable.
 *
 * Since the ADR-0019 migration the column holds **local wall-clock**, so the
 * digits in either shape are already the answer and no zone conversion may be
 * applied. Converting would be the bug, not the feature: it would add today's
 * UTC offset to a value that never carried one, and the result would move
 * twice a year — exactly the drift ADR-0019 removed. The epoch date Prisma
 * attaches is meaningless and is discarded rather than resolved.
 *
 * @param value the raw bound, in either shape
 * @returns `HH:MM`, or null when there is nothing renderable
 */
export const formatStoredTimeOfDay = (
    value: string | null | undefined,
): string | null => {
    if (!value) return null;

    const plain = HH_MM_SS.exec(value.trim());
    if (plain) return `${plain[1]}:${plain[2]}`;

    const iso = ISO_TIME_OF_DAY.exec(value);
    if (!iso) return null;

    // The time-of-day carried by the epoch instant IS the local wall-clock the
    // operator configured; the date around it is discarded, not resolved.
    return `${iso[1]}:${iso[2]}`;
};

/**
 * The exact inverse of `formatStoredTimeOfDay`: what the operator types as
 * local wall-clock (the tariff editor's time inputs) is what gets stored.
 *
 * Since ADR-0019 that is an identity on the digits — the column holds local
 * wall-clock and both matchers compare in `Europe/Berlin`, so a typed 13:00
 * stores as `13:00:00` and prices from 13 Uhr in December as well as in July.
 * The pre-migration version shifted it to the UTC convention (13:00 → 11:00);
 * doing that now would re-create the drift one row at a time.
 *
 * Round-trip property: `formatStoredTimeOfDay(localTimeOfDayToStored(x)) === x`,
 * on every date rather than only within one day.
 *
 * @param local `HH:MM` as read off a clock at the station
 * @returns `HH:MM:SS` to store, or null when unparsable
 */
export const localTimeOfDayToStored = (
    local: string | null | undefined,
): string | null => {
    if (!local) return null;
    const m = /^(\d{2}):(\d{2})$/.exec(local.trim());
    if (!m) return null;
    return `${m[1]}:${m[2]}:00`;
};

const LOCAL_INPUT = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/;

/**
 * Renders a stored **instant** as the value an `<input type="datetime-local">`
 * expects: wall-clock with no zone — the STATION's wall clock (ADR-0019), so
 * "local" here means local to the station, not to the operator's browser.
 *
 * Note how this differs from `formatStoredTimeOfDay` a few lines up: that one
 * must NOT convert, because an ADR-0019 `pricescale` bound already holds local
 * wall-clock. A setup time is the opposite — a genuine instant, stored with a
 * `Z` — so here the offset has to be applied, and leaving it out is the bug.
 * The two live together so the next reader has to decide which kind of value
 * they hold before reaching for one.
 *
 * What #2982 shipped was `new Date(instant).toISOString().slice(0, 16)`, which
 * yields the **UTC** wall clock. The input then showed 06.10. 22:00 for an
 * instant that is 07.10. 00:00 in Berlin, and `localInputValueToInstant` read
 * those digits back as local — so opening the field and saving it untouched
 * moved the stored instant two hours earlier, every time. The fix for that
 * read the browser's zone instead, which is right only for an operator whose
 * browser is in Berlin.
 *
 * Round-trip property: `localInputValueToInstant(instantToLocalInputValue(x))`
 * is `x` truncated to the minute, in any runner zone and on either side of a
 * DST change (except the repeated hour, see the tests).
 *
 * @param instant an ISO instant, e.g. `'2026-10-06T22:00:00.000Z'`
 * @returns `YYYY-MM-DDTHH:mm` on the station's clock, or `''` when unusable
 */
export const instantToLocalInputValue = (
    instant: string | null | undefined,
    timeZone: string = STATION_TIME_ZONE,
): string => formatAtStation(instant, "yyyy-MM-dd'T'HH:mm", 'en', timeZone);

/**
 * The STATION calendar date of an instant, for a `<input type="date">` — the
 * inverse of saving a picked day as station midnight (`stationInstant`) or
 * station end of day (`stationDayEnd`). The UTC date of the ISO string
 * (`iso.slice(0, 10)`) is the previous day for anything stored near midnight
 * east of UTC: a banner starting 01.11. read back as 31.10. and moved a day
 * earlier on every save.
 *
 * @returns `YYYY-MM-DD`, or `''` when unusable
 */
export const instantToLocalDateInputValue = (
    instant: string | null | undefined,
): string => instantToLocalInputValue(instant).slice(0, 10);

/**
 * The exact inverse: what the operator types is the station's wall clock, and
 * that is what gets stored.
 *
 * @param value `YYYY-MM-DDTHH:mm` as typed into a `datetime-local` input
 * @returns the ISO instant to send, or null when unparsable
 */
export const localInputValueToInstant = (
    value: string | null | undefined,
    timeZone: string = STATION_TIME_ZONE,
): string | null => {
    if (!value) return null;
    const m = LOCAL_INPUT.exec(value.trim());
    if (!m) return null;
    const date = stationInstant(`${m[1]}-${m[2]}-${m[3]}`, `${m[4]}:${m[5]}`, timeZone);
    if (Number.isNaN(date.getTime())) return null;
    return date.toISOString();
};
